import { useCallback, useMemo, useState } from 'react';
import { FileArchive, FilePlus, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useRunDceUpload } from '../../hooks';
import type { DceSourceType } from '../../types/dce-upload.types';
import { DceUploadProgress } from './DceUploadProgress';

interface DceUploadDialogProps {
  opportunityId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone?: (uploadId: number) => void;
}

interface SelectedFiles {
  sourceType: DceSourceType;
  files: File[];
}

const ZIP_MIME_TYPES = ['application/zip', 'application/x-zip-compressed'];
const ZIP_EXTENSIONS = ['.zip'];

function inferSourceType(files: File[]): DceSourceType {
  if (
    files.length === 1 &&
    (ZIP_MIME_TYPES.includes(files[0]?.type ?? '') ||
      ZIP_EXTENSIONS.some((ext) => files[0]?.name.toLowerCase().endsWith(ext)))
  ) {
    return 'ZIP';
  }
  return 'LOOSE_FILES';
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} Go`;
}

export function DceUploadDialog({
  opportunityId,
  open,
  onOpenChange,
  onDone,
}: DceUploadDialogProps) {
  const [selected, setSelected] = useState<SelectedFiles | null>(null);
  const [perFileProgress, setPerFileProgress] = useState<Record<number, number>>({});
  const { run, isRunning, uploadId, error } = useRunDceUpload();

  const totalSize = useMemo(
    () => selected?.files.reduce((acc, f) => acc + f.size, 0) ?? 0,
    [selected],
  );

  const aggregatedRatio = useMemo(() => {
    if (!selected) return 0;
    const loaded = Object.entries(perFileProgress).reduce((acc, [idx, ratio]) => {
      const file = selected.files[Number(idx)];
      return acc + (file ? file.size * ratio : 0);
    }, 0);
    return totalSize > 0 ? loaded / totalSize : 0;
  }, [perFileProgress, selected, totalSize]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    setSelected({ sourceType: inferSourceType(files), files });
    setPerFileProgress({});
  }, []);

  const handleStart = useCallback(async () => {
    if (!selected) return;
    try {
      const finalUploadId = await run({
        opportunityId,
        sourceType: selected.sourceType,
        files: selected.files,
        onProgress: (evt) => {
          setPerFileProgress((prev) => ({
            ...prev,
            [evt.fileIndex]: evt.ratio ?? prev[evt.fileIndex] ?? 0,
          }));
        },
      });
      onDone?.(finalUploadId);
    } catch {
      // L'erreur est exposée via le hook.
    }
  }, [run, selected, opportunityId, onDone]);

  const handleClose = useCallback(() => {
    if (isRunning) return; // bloque la fermeture pendant l'upload
    setSelected(null);
    setPerFileProgress({});
    onOpenChange(false);
  }, [isRunning, onOpenChange]);

  const phase: 'idle' | 'uploading' | 'processing' = !uploadId
    ? isRunning
      ? 'uploading'
      : 'idle'
    : aggregatedRatio < 1
      ? 'uploading'
      : 'processing';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Importer un DCE</DialogTitle>
          <DialogDescription>
            Déposez un ZIP du dossier de consultation, ou plusieurs fichiers
            séparés (PDF, DOCX, XLSX, plans). Les pièces seront classées dans
            une prochaine étape.
          </DialogDescription>
        </DialogHeader>

        {!selected && (
          <label className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/30 p-10 text-center cursor-pointer hover:border-primary transition-colors">
            <Upload className="h-10 w-10 text-muted-foreground" />
            <div className="text-sm">
              <span className="font-medium text-primary">Cliquer pour sélectionner</span>
              <span className="text-muted-foreground"> ou glisser-déposer</span>
            </div>
            <p className="text-xs text-muted-foreground">
              ZIP (recommandé) ou fichiers PDF/DOCX/XLSX — max 200 Mo par fichier
            </p>
            <input
              type="file"
              multiple
              className="hidden"
              accept=".zip,.pdf,.docx,.doc,.xlsx,.xls,.dwg,.dxf"
              onChange={handleFileChange}
            />
          </label>
        )}

        {selected && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm">
              {selected.sourceType === 'ZIP' ? (
                <FileArchive className="h-4 w-4 text-copper" />
              ) : (
                <FilePlus className="h-4 w-4 text-copper" />
              )}
              <span className="font-medium">
                {selected.sourceType === 'ZIP' ? 'Archive ZIP' : 'Fichiers séparés'}
              </span>
              <span className="text-muted-foreground">
                · {selected.files.length} fichier
                {selected.files.length > 1 ? 's' : ''} · {formatBytes(totalSize)}
              </span>
              {!isRunning && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto"
                  onClick={() => setSelected(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            <ul className="max-h-40 overflow-y-auto space-y-1 text-sm border rounded-md p-2">
              {selected.files.map((f, idx) => (
                <li key={idx} className="flex justify-between items-center">
                  <span className="truncate flex-1">{f.name}</span>
                  <span className="text-xs text-muted-foreground ml-2">
                    {formatBytes(f.size)}
                  </span>
                </li>
              ))}
            </ul>

            {(isRunning || uploadId) && (
              <DceUploadProgress
                uploadId={uploadId}
                uploadPhaseRatio={aggregatedRatio}
                phase={phase}
              />
            )}

            {error && (
              <p className="text-sm text-red-600">Erreur : {error.message}</p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isRunning}>
            {uploadId ? 'Fermer' : 'Annuler'}
          </Button>
          {selected && !uploadId && (
            <Button onClick={handleStart} disabled={isRunning}>
              {isRunning ? 'Upload en cours…' : 'Démarrer l\'import'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
